/* Parses an OPML XML string and extracts podcast feed entries.
   Uses the browser DOMParser — no external dependencies. */

export interface ParsedPodcast {
  name: string;
  feedUrl: string;
}

export interface ParseOpmlResult {
  podcasts: ParsedPodcast[];
  errors: string[];
}

export function parseOpml(xmlString: string): ParseOpmlResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "text/xml");

  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    return { podcasts: [], errors: ["Invalid XML: could not parse the file."] };
  }

  const podcasts: ParsedPodcast[] = [];
  const errors: string[] = [];
  const seenUrls = new Set<string>();

  const outlines = doc.querySelectorAll("outline");

  for (const outline of outlines) {
    const xmlUrl = getAttrCaseInsensitive(outline, "xmlUrl");
    if (!xmlUrl) continue;

    const normalized = xmlUrl.toLowerCase().trim();
    if (seenUrls.has(normalized)) continue;
    seenUrls.add(normalized);

    const name =
      getAttrCaseInsensitive(outline, "text") ||
      getAttrCaseInsensitive(outline, "title") ||
      xmlUrl;

    podcasts.push({ name: name.trim(), feedUrl: xmlUrl.trim() });
  }

  if (podcasts.length === 0 && outlines.length > 0) {
    errors.push("No feed URLs found in the OPML file.");
  }
  if (podcasts.length === 0 && outlines.length === 0) {
    errors.push("The file does not appear to be a valid OPML file.");
  }

  return { podcasts, errors };
}

function getAttrCaseInsensitive(
  el: Element,
  attrName: string,
): string | null {
  // Try exact match first
  const exact = el.getAttribute(attrName);
  if (exact) return exact;

  // Fall back to case-insensitive search
  for (const attr of el.attributes) {
    if (attr.name.toLowerCase() === attrName.toLowerCase()) {
      return attr.value;
    }
  }
  return null;
}
